import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useClients } from "@/context/ClientContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { StatusBadge, PlatformBadge, ModeBadge, STATUS_LABEL, EDITABLE_STATUS_LABEL, PLATFORM_LABEL } from "@/components/Badges";
import { AutopilotDialog } from "@/components/AutopilotDialog";
import { TikTokPublishDialog } from "@/components/TikTokPublishDialog";
import QueueStartDialog from "@/components/QueueStartDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  CalendarDays, List, Play, ChevronLeft, ChevronRight, Filter, CalendarClock, Zap, Hand, Eye, X, Wand2, Send, Plus, Trash2, RefreshCw,
} from "lucide-react";

const MODE_ICON = { simulation: Eye, assisted: Hand, api: Zap };
const EMPTY_POST = { project_id: "", platform: "linkedin", account_id: "", asset_id: "", caption: "", link: "", scheduled_at: "", mode: "simulation" };
const CREDENTIAL_LABEL = {
  standard: "Comrocket Standard",
  tenant: "Mandanteneigene App",
  environment: "Server-Standard",
};

export default function Redaktionsplan() {
  const { user } = useAuth();
  const readOnlyReview = Boolean(user?.review_access?.read_only);
  const { current } = useClients();
  const [view, setView] = useState("list");
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [filters, setFilters] = useState({ project_id: "", platform: "", status: "", series: "" });
  const [selected, setSelected] = useState(new Set());
  const selectionAnchorRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [busy, setBusy] = useState(false);
  const [autopilot, setAutopilot] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiVariants, setAiVariants] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [youtubeTitle, setYoutubeTitle] = useState("");
  const [youtubeTitleBusy, setYoutubeTitleBusy] = useState(false);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState(EMPTY_POST);
  const [syncing, setSyncing] = useState(false);
  const [tiktokPost, setTiktokPost] = useState(null);
  const [queueStart, setQueueStart] = useState(false);

  const load = async () => {
    if (!current) return;
    const params = new URLSearchParams({ client_id: current.id, editorial_only: "true" });
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const { data } = await api.get(`/posts?${params.toString()}`);
    setPosts(data);
    setSelected(new Set());
    selectionAnchorRef.current = null;
  };

  useEffect(() => {
    if (!current) return;
    setTiktokPost(null);
    api.get(`/projects?client_id=${current.id}`).then(({ data }) => {
      setProjects(data);
      setNewPost((form) => ({ ...form, project_id: form.project_id || data[0]?.id || "" }));
    });
    api.get(`/accounts?client_id=${current.id}`).then(({ data }) => setAccounts(data));
    api.get(`/media/assets?client_id=${current.id}`).then(({ data }) => setAssets(data));
  }, [current]);
  useEffect(() => { load(); }, [current, filters]);
  useEffect(() => {
    if (!current) return undefined;
    const refresh = async () => {
      if (document.visibilityState !== "visible") return;
        const params = new URLSearchParams({ client_id: current.id, editorial_only: "true" });
      Object.entries(filters).forEach(([key, value]) => value && params.append(key, value));
      try {
        const { data } = await api.get(`/posts?${params.toString()}`);
        setPosts(data);
        setDetail((openPost) => openPost ? (data.find((post) => post.id === openPost.id) || openPost) : null);
      } catch {
        // Keep the last known state; the next interval retries automatically.
      }
    };
    const interval = window.setInterval(refresh, 60 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [current, filters]);

  const syncStatuses = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/social/sync-published", { client_id: current.id });
      const failed = (data.results || []).filter((row) => row.status === "error");
      await load();
      toast.success(`${data.created || 0} neue und ${data.updated || 0} vorhandene Plattform-Posts abgeglichen.`);
      if (failed.length) {
        toast.warning(failed.map((row) => `${row.platform}: ${row.message || "Abgleich fehlgeschlagen"}`).join(" · "));
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (p) => {
    setDetail(p); setReadiness(null);
    setAiTopic(p.caption || ""); setAiVariants([]);
    setYoutubeTitle(p.youtube_title || "");
    try { const { data } = await api.get(`/posts/${p.id}/readiness`); setReadiness(data); } catch {}
  };

  const generateCaptions = async () => {
    if (!aiTopic.trim()) { toast.error("Bitte Thema oder Ausgangstext eingeben"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/captions", {
        topic: aiTopic.trim(), platform: detail.platform, tone: "professionell", hashtags: true, count: 3,
      });
      setAiVariants(data.variants || []);
      toast.success("3 KI-Vorschläge erstellt");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const applyCaption = async (caption) => {
    try {
      const { data } = await api.patch(`/posts/${detail.id}`, { caption });
      setDetail(data); setAiTopic(caption); setAiVariants([]);
      toast.success("Caption übernommen");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveYoutubeTitle = async () => {
    const title = youtubeTitle.trim();
    if (!title) { toast.error("Der YouTube-Titel darf nicht leer sein"); return; }
    if (title.length > 100) { toast.error("Der YouTube-Titel darf höchstens 100 Zeichen haben"); return; }
    setYoutubeTitleBusy(true);
    try {
      const { data } = await api.patch(`/posts/${detail.id}`, { youtube_title: title });
      setDetail(data); setYoutubeTitle(data.youtube_title || "");
      toast.success("YouTube-Titel gespeichert");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setYoutubeTitleBusy(false); }
  };

  const createSinglePost = async () => {
    if (!newPost.project_id) { toast.error("Bitte Projekt auswählen"); return; }
    setBusy(true);
    try {
      const account = accounts.find((item) => item.id === newPost.account_id);
      const asset = assets.find((item) => item.id === newPost.asset_id);
      const payload = {
        client_id: current.id,
        project_id: newPost.project_id,
        platform: newPost.platform,
        account_id: account?.id || null,
        account_handle: account?.handle || null,
        caption: newPost.caption,
        link: newPost.link,
        asset_id: asset?.id || null,
        asset_filename: asset?.filename || null,
        format: asset?.format || null,
        scheduled_at: newPost.scheduled_at ? new Date(newPost.scheduled_at).toISOString() : null,
        mode: newPost.mode,
      };
      const { data } = await api.post("/posts", payload);
      toast.success("Einzelpost als Entwurf erstellt");
      setNewPost({ ...EMPTY_POST, project_id: newPost.project_id });
      setNewPostOpen(false);
      await load();
      openDetail(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = (id, shiftKey = false) => {
    setSelected((currentSelection) => {
      const next = new Set(currentSelection);
      const anchorIndex = posts.findIndex((post) => post.id === selectionAnchorRef.current);
      const clickedIndex = posts.findIndex((post) => post.id === id);
      if (shiftKey && anchorIndex >= 0 && clickedIndex >= 0) {
        const shouldSelect = !currentSelection.has(id);
        const [start, end] = [anchorIndex, clickedIndex].sort((a, b) => a - b);
        posts.slice(start, end + 1).forEach((post) => {
          if (shouldSelect) next.add(post.id);
          else next.delete(post.id);
        });
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      selectionAnchorRef.current = id;
      return next;
    });
  };
  const allPostsSelected = posts.length > 0 && posts.every((post) => selected.has(post.id));
  const somePostsSelected = posts.some((post) => selected.has(post.id));
  const toggleAllPosts = () => {
    setSelected(allPostsSelected ? new Set() : new Set(posts.map((post) => post.id)));
    selectionAnchorRef.current = null;
  };

  const bulk = async (action, value, days) => {
    if (selected.size === 0) { toast.error("Keine Posts ausgewählt"); return; }
    try {
      const { data } = await api.post("/posts/bulk", { post_ids: [...selected], action, value, days });
      toast.success(`${data.affected} Posts aktualisiert`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) { toast.error("Keine Posts ausgewählt"); return; }
    const published = posts.filter((p) => selected.has(p.id) && p.status === "veroeffentlicht").length;
    const warning = published
      ? `\n\n${published} bereits veröffentlichte Post(s) werden als Nachweis behalten und nicht gelöscht.`
      : "";
    if (!window.confirm(`${ids.length - published} geplante/noch nicht veröffentlichte Post(s) dauerhaft aus Comrocket löschen?${warning}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts/bulk-delete", { client_id: current.id, ids });
      toast.success(`${data.deleted} Post(s) gelöscht`);
      if (data.protected) toast.info(`${data.protected} veröffentlichte Post(s) wurden behalten.`);
      setSelected(new Set());
      setDetail(null);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const runQueue = async () => {
    setQueueStart(true);
  };

  const publishNow = async (ids) => {
    if (!ids.length) return;
    const targets = posts.filter((p) => ids.includes(p.id));
    if (targets.some(p => p.platform === "tiktok")) {
      if (ids.length !== 1) {
        toast.error("TikTok-Posts bitte einzeln mit Vorschau und Zustimmung veröffentlichen");
        return;
      }
      setDetail(null);
      setTiktokPost(targets[0]);
      return;
    }
    if (targets.some((p) => p.mode !== "api")) {
      toast.error("Nur Posts im Modus API-Automatik dürfen echt veröffentlicht werden");
      return;
    }
    if (!window.confirm(`${ids.length} Post(s) JETZT echt veröffentlichen?\n\nVerbundene Plattformen (z.B. LinkedIn) werden live gepostet. Nicht verbundene laufen als Testlauf.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/social/publish-bulk", { post_ids: ids });
      const parts = [];
      if (data.published) parts.push(`${data.published} veröffentlicht`);
      if (data.dry_run) parts.push(`${data.dry_run} Testlauf`);
      if (data.errors) parts.push(`${data.errors} Fehler`);
      if (data.skipped) parts.push(`${data.skipped} übersprungen`);
      const unsuccessful = (data.results || []).filter((result) => result.status !== "published");
      const reasons = [...new Set(unsuccessful.map((result) => result.message).filter(Boolean))];
      const summary = [parts.join(" · "), ...reasons].filter(Boolean).join(": ") || "Fertig";
      (data.errors || data.dry_run || data.skipped ? toast.error : toast.success)(summary);
      setDetail(null); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // calendar grid
  const calDays = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    const first = new Date(y, m, 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [month]);

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => { if (p.scheduled_at) { const k = p.scheduled_at.slice(0, 10); (map[k] = map[k] || []).push(p); } });
    return map;
  }, [posts]);

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  return (
    <div>
      {tiktokPost && <TikTokPublishDialog key={`${current.id}:${tiktokPost.id}`} post={tiktokPost} scheduled={!!tiktokPost._scheduleDialog} onClose={() => setTiktokPost(null)} onDone={load} />}
      {queueStart && <QueueStartDialog key={`${current.id}:${filters.project_id}`} clientId={current.id} projectId={filters.project_id}
        onClose={() => setQueueStart(false)} onDone={load} />}
      <PageHeader overline={current.name} title="Redaktionsplan & Queue" subtitle="Plane, filtere und veröffentliche Posts – als Liste oder Kalender."
        actions={readOnlyReview ? (
          <div className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700">
            Schreibgeschützter Review-Zugang
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button data-testid="new-post-open" onClick={() => setNewPostOpen(true)} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Plus size={15} className="mr-1.5" /> Neuer Post</Button>
            <div className="flex bg-white border border-slate-200 rounded-md p-0.5">
              <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-1.5 rounded-sm text-sm font-medium flex items-center gap-1.5 ${view === "list" ? "bg-[#0A0A0A] text-white" : "text-slate-500"}`}><List size={15} /> Liste</button>
              <button data-testid="calendar-view-toggle" onClick={() => setView("calendar")} className={`px-3 py-1.5 rounded-sm text-sm font-medium flex items-center gap-1.5 ${view === "calendar" ? "bg-[#0A0A0A] text-white" : "text-slate-500"}`}><CalendarDays size={15} /> Kalender</button>
            </div>
            {!user?.review_access && <Button data-testid="autopilot-open-btn" onClick={() => setAutopilot(true)} variant="outline" className="rounded-full font-semibold"><Wand2 size={15} className="mr-1.5" /> Autopilot</Button>}
            {!user?.review_access && <Button data-testid="queue-run-btn" onClick={runQueue} disabled={busy} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Play size={15} className="mr-1.5" /> Queue starten</Button>}
          </div>
        )} />

      {!readOnlyReview && <AutopilotDialog open={autopilot} onOpenChange={setAutopilot} clientId={current.id} projectId={filters.project_id} onApplied={load} />}

      <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-post-dialog">
          <DialogHeader><DialogTitle className="font-display">Neuen Einzelpost erstellen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Projekt</Label><Select value={newPost.project_id || undefined} onValueChange={(value) => setNewPost({ ...newPost, project_id: value })}><SelectTrigger className="mt-1"><SelectValue placeholder="Projekt auswählen" /></SelectTrigger><SelectContent>{projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Plattform</Label><Select value={newPost.platform} onValueChange={(value) => setNewPost({ ...newPost, platform: value, account_id: "" })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PLATFORM_LABEL).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Account</Label><Select value={newPost.account_id || "__none__"} onValueChange={(value) => setNewPost({ ...newPost, account_id: value === "__none__" ? "" : value })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Noch nicht zuordnen</SelectItem>{accounts.filter((account) => account.platform === newPost.platform).map((account) => <SelectItem key={account.id} value={account.id}>{account.handle}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Bild/Video</Label><Select value={newPost.asset_id || "__none__"} onValueChange={(value) => setNewPost({ ...newPost, asset_id: value === "__none__" ? "" : value })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Kein Asset</SelectItem>{assets.map((asset) => <SelectItem key={asset.id} value={asset.id}>{asset.filename}</SelectItem>)}</SelectContent></Select></div>
            <div className="sm:col-span-2"><Label>Caption oder Thema</Label><Textarea className="mt-1 min-h-[110px]" value={newPost.caption} onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })} placeholder="Caption direkt eingeben oder nur das Thema – anschließend kann die KI Varianten erstellen." /></div>
            <div className="sm:col-span-2"><Label>Link (optional)</Label><Input className="mt-1" value={newPost.link} onChange={(e) => setNewPost({ ...newPost, link: e.target.value })} placeholder="https://…" /></div>
            <div><Label>Termin (optional)</Label><Input className="mt-1" type="datetime-local" value={newPost.scheduled_at} onChange={(e) => setNewPost({ ...newPost, scheduled_at: e.target.value })} /></div>
            <div><Label>Modus</Label><Select value={newPost.mode} onValueChange={(value) => setNewPost({ ...newPost, mode: value })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="simulation">Simulation</SelectItem><SelectItem value="assisted">Assistiert</SelectItem><SelectItem value="api">API-Automatik</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewPostOpen(false)}>Abbrechen</Button><Button data-testid="new-post-save" disabled={busy} onClick={createSinglePost}>Als Entwurf speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter size={15} className="text-slate-400" />
        <FilterSelect testid="filter-project" value={filters.project_id} onChange={(v) => setFilters((f) => ({ ...f, project_id: v }))} placeholder="Projekt" options={projects.map((p) => [p.id, p.name])} />
        <FilterSelect testid="filter-platform" value={filters.platform} onChange={(v) => setFilters((f) => ({ ...f, platform: v }))} placeholder="Plattform" options={Object.entries(PLATFORM_LABEL)} />
        <FilterSelect testid="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} placeholder="Status" options={Object.entries(STATUS_LABEL)} />
        <FilterSelect testid="filter-series" value={filters.series} onChange={(v) => setFilters((f) => ({ ...f, series: v }))} placeholder="Serie" options={[["A", "A"], ["B", "B"], ["C", "C"], ["D", "D"]]} />
        {Object.values(filters).some(Boolean) && <button className="text-xs text-slate-500 underline" onClick={() => setFilters({ project_id: "", platform: "", status: "", series: "" })}>Filter zurücksetzen</button>}
        {!readOnlyReview && <Button variant="outline" size="sm" onClick={syncStatuses} disabled={syncing} data-testid="sync-post-status-btn" className="ml-auto rounded-full">
          <RefreshCw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
          Status synchronisieren
        </Button>}
        <span className="text-sm text-slate-400 font-mono">{posts.length} Posts</span>
      </div>

      {/* Bulk bar */}
      {!readOnlyReview && selected.size > 0 && (
        <div data-testid="bulk-bar" className="sticky top-16 z-40 flex flex-wrap items-center gap-3 mb-3 bg-[#0A0A0A] text-white rounded-md px-4 py-2.5 shadow-lg">
          <span className="text-sm font-semibold">{selected.size} ausgewählt</span>
          <div className="h-4 w-px bg-white/20" />
          <BulkSelect placeholder="Status ändern" onChange={(v) => bulk("status", v)} options={Object.entries(EDITABLE_STATUS_LABEL)} testid="bulk-status" />
          <BulkSelect placeholder="Modus setzen" onChange={(v) => bulk("mode", v)} options={[["simulation", "Simulation"], ["assisted", "Assistiert"], ["api", "API-Automatik"], ["manual", "Manuell / extern"]]} testid="bulk-mode" />
          <Button size="sm" variant="secondary" className="h-8" onClick={() => bulk("shift", null, 1)} data-testid="bulk-shift">+1 Tag verschieben</Button>
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || posts.filter((p) => selected.has(p.id)).some((p) => p.mode !== "api")} onClick={() => publishNow([...selected])} data-testid="bulk-publish"><Send size={14} className="mr-1.5" /> Jetzt veröffentlichen</Button>
          <Button size="sm" variant="destructive" className="h-8" disabled={busy} onClick={deleteSelected} data-testid="bulk-delete"><Trash2 size={14} className="mr-1.5" /> Löschen</Button>
          <button className="ml-auto" onClick={() => { setSelected(new Set()); selectionAnchorRef.current = null; }}><X size={16} /></button>
        </div>
      )}

      {posts.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Noch keine Posts" hint="Starte einen Bulk-Import, um den Redaktionsplan zu füllen." />
      ) : view === "list" ? (
        <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      disabled={readOnlyReview}
                      checked={allPostsSelected ? true : (somePostsSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAllPosts}
                      aria-label="Alle angezeigten Posts auswählen"
                      data-testid="plan-check-all"
                    />
                  </th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Plattform</th>
                  <th className="px-3 py-2 font-semibold">Projekt</th>
                  <th className="px-3 py-2 font-semibold">Account</th>
                  <th className="px-3 py-2 font-semibold">Serie</th>
                  <th className="px-3 py-2 font-semibold">YouTube-Titel</th>
                  <th className="px-3 py-2 font-semibold">Caption</th>
                  <th className="px-3 py-2 font-semibold">Termin</th>
                  <th className="px-3 py-2 font-semibold">Veröffentlichungsart</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {posts.map((p) => (
                  <tr key={p.id} data-testid={`plan-row-${p.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(p)}>
                    <td
                      className="px-3 py-2"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!readOnlyReview) toggle(p.id, event.shiftKey);
                      }}
                      title={readOnlyReview ? "Im Review schreibgeschützt" : "Mit Shift-Klick einen Bereich auswählen"}
                    >
                      <Checkbox disabled={readOnlyReview} checked={selected.has(p.id)} data-testid={`plan-check-${p.id}`} />
                    </td>
                    <td className="px-3 py-2"><StatusBadge post={p} /></td>
                    <td className="px-3 py-2"><PlatformBadge platform={p.platform} /></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{p.project_name || projects.find((project) => project.id === p.project_id)?.name || "–"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.account_handle || "–"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.series || "–"}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate text-slate-600" title={p.youtube_title || ""}>{p.youtube_title || "—"}</td>
                    <td className="px-3 py-2 max-w-[280px] truncate text-slate-600">{p.caption || "—"}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : <span className="text-amber-600">fehlt</span>}</td>
                    <td className="px-3 py-2"><ModeBadge mode={p.mode} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="rounded-md border-slate-200 shadow-none p-4">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-md"><ChevronLeft size={18} /></button>
            <div className="font-display font-bold text-lg">{month.toLocaleString("de-DE", { month: "long", year: "numeric" })}</div>
            <button data-testid="cal-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-md"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="bg-slate-50 text-center py-2 text-xs font-semibold text-slate-500">{d}</div>)}
            {calDays.map((day, i) => {
              const key = day ? `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}` : null;
              const dayPosts = key ? postsByDay[key] || [] : [];
              return (
                <div key={i} className="bg-white min-h-[104px] p-1.5">
                  {day && <div className="text-xs text-slate-400 mb-1">{day.getDate()}</div>}
                  <div className="space-y-1">
                    {dayPosts.slice(0, 3).map((p) => (
                      <button key={p.id} onClick={() => openDetail(p)} data-testid={`cal-post-${p.id}`}
                        className="w-full text-left text-[10px] px-1.5 py-0.5 rounded-sm truncate hover:opacity-80 border"
                        style={{}} title={p.caption}>
                        <StatusBadge post={p} /> <span className="text-slate-500">{PLATFORM_LABEL[p.platform]}</span>
                      </button>
                    ))}
                    {dayPosts.length > 3 && <div className="text-[10px] text-slate-400 px-1.5">+{dayPosts.length - 3} weitere</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display">Post-Details</SheetTitle></SheetHeader>
              <div className="mt-5 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <PlatformBadge platform={detail.platform} /><StatusBadge post={detail} /><ModeBadge mode={detail.mode} />
                </div>
                <Field label="Caption" value={detail.caption} mono={false} />
                {detail.platform === "youtube" && (
                  <div className="border border-red-200 rounded-md p-3 bg-red-50/40">
                    <div className="overline mb-2">YouTube-Titel</div>
                    <Input
                      data-testid="youtube-title-input"
                      value={youtubeTitle}
                      maxLength={100}
                      onChange={(e) => setYoutubeTitle(e.target.value)}
                      className="bg-white"
                    />
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-500">{youtubeTitle.length}/100 Zeichen</span>
                      <Button
                        data-testid="youtube-title-save"
                        size="sm"
                        variant="outline"
                        disabled={youtubeTitleBusy || !youtubeTitle.trim() || youtubeTitle.trim() === (detail.youtube_title || "")}
                        onClick={saveYoutubeTitle}
                        className="bg-white"
                      >
                        {youtubeTitleBusy ? "Speichert …" : "Titel speichern"}
                      </Button>
                    </div>
                    {detail.status === "veroeffentlicht" && (
                      <div className="mt-2 text-xs text-amber-700">Ändert nur den CMS-Eintrag, nicht rückwirkend das bereits veröffentlichte YouTube-Video.</div>
                    )}
                  </div>
                )}
                {!readOnlyReview && <div className="border border-violet-200 rounded-md p-3 bg-violet-50/50">
                  <div className="overline mb-2 flex items-center gap-1.5"><Wand2 size={13} /> KI-Caption</div>
                  <Textarea
                    data-testid="ai-caption-topic"
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="Thema oder vorhandene Caption eingeben …"
                    className="bg-white min-h-[88px]"
                  />
                  <Button
                    data-testid="ai-caption-generate"
                    size="sm"
                    variant="outline"
                    disabled={aiBusy}
                    onClick={generateCaptions}
                    className="mt-2 w-full bg-white"
                  >
                    <Wand2 size={14} className="mr-1.5" /> {aiBusy ? "KI arbeitet …" : "3 Vorschläge erstellen"}
                  </Button>
                  {aiVariants.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {aiVariants.map((variant, index) => (
                        <div key={index} className="rounded-md border border-violet-100 bg-white p-2.5">
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">{variant}</div>
                          <Button size="sm" className="mt-2 h-7" onClick={() => applyCaption(variant)}>Übernehmen</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>}
                <Field label="Account" value={detail.account_handle} />
                <Field label="Mandant" value={detail.client_name || current.name} mono={false} />
                <Field label="Projekt" value={detail.project_name || projects.find((project) => project.id === detail.project_id)?.name} mono={false} />
                <Field label="App-Verbindung" value={CREDENTIAL_LABEL[detail.credential_source] || "Noch nicht am Account gespeichert"} mono={false} />
                <Field label="Job-/Post-ID" value={detail.id} />
                <Field label="Bulk-Import" value={detail.import_id || "Einzelpost"} />
                {(detail.asset_filenames?.length || detail.asset_filename) ? <Field
                  label={(detail.asset_filenames?.length || detail.asset_ids?.length) > 1 ? "Assets" : "Asset"}
                  value={detail.asset_filenames?.length
                    ? detail.asset_filenames.join("\n")
                    : detail.asset_filename}
                  mono={false}
                /> : <Field label="Beitragstyp" value="Textbeitrag" mono={false} />}
                {detail.format && <Field label="Format" value={detail.format} />}
                <Field label="Veröffentlichung" value={detail.scheduled_at ? new Date(detail.scheduled_at).toLocaleString("de-DE") : "Sofort"} mono={false} />
                <Field label="Serie" value={detail.series} />
                {detail.warnings?.length > 0 && (
                  <div><div className="overline mb-1">Warnungen</div>{detail.warnings.map((w, i) => <div key={i} className="text-xs text-amber-600">• {w}</div>)}</div>
                )}
                {readiness && (
                  <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                    <div className="overline mb-2">API-Bereitschaft</div>
                    <div className="text-sm space-y-1">
                      <div>Modus: <b>{readiness.mode}</b></div>
                      <div>Zielaccount: <span className="font-mono">{readiness.target_account || "—"}</span></div>
                      <div>Mandant / Projekt: <b>{readiness.client_name || current.name}</b> / {readiness.project_name || "—"}</div>
                      <div>App-Verbindung: {CREDENTIAL_LABEL[readiness.credential_source] || "—"}</div>
                      <div>API bereit: {readiness.api_ready ? <span className="text-emerald-600 font-semibold">Ja</span> : <span className="text-slate-500">Nein</span>}</div>
                      {readiness.blockers.map((b, i) => <div key={i} className="text-rose-600 text-xs">⛔ {b}</div>)}
                      <div className="text-xs text-slate-500 mt-1">Nächster Schritt: {readiness.next_step}</div>
                    </div>
                  </div>
                )}
                {!readOnlyReview && <div className="flex flex-wrap gap-2 pt-2">
                  {["geprueft", "bereit", "geplant"].map((s) => (
                    <Button key={s} size="sm" variant="outline" data-testid={`detail-status-${s}`} onClick={async () => { await api.patch(`/posts/${detail.id}`, { status: s }); toast.success("Status geändert"); setDetail({ ...detail, status: s }); load(); }}>{STATUS_LABEL[s]}</Button>
                  ))}
                </div>}
                {!readOnlyReview && detail.platform === "tiktok" && detail.mode === "api" && new Date(detail.scheduled_at).getTime() > Date.now() && (detail.status === "bereit" || detail.tiktok_schedule) && <Button data-testid="detail-tiktok-schedule" disabled={busy} onClick={() => { setTiktokPost({ ...detail, _scheduleDialog: true }); setDetail(null); }} className="w-full">
                  TikTok zeitgesteuert freigeben
                </Button>}
                {detail.platform === "tiktok" && detail.status === "geplant" && detail.automation_eligible && detail.tiktok_schedule?.state === "approved" && <p className="text-sm text-emerald-700">TikTok-Automatik freigegeben für den hinterlegten Termin. Änderungen erfordern eine neue Freigabe.</p>}
                {detail.platform === "tiktok" && detail.tiktok_schedule?.state === "review_required" && <p className="text-sm text-amber-700">TikTok-Versand angehalten: {detail.last_error || "Ergebnis oder Freigabe prüfen"}. Es erfolgt keine automatische Wiederholung.</p>}
                {!readOnlyReview && <Button data-testid="detail-publish-now" disabled={busy || (detail.mode !== "api" && detail.platform !== "tiktok")} onClick={() => publishNow([detail.id])} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Send size={15} className="mr-1.5" /> Jetzt veröffentlichen
                </Button>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Field = ({ label, value, mono = true }) => (
  <div><div className="overline mb-0.5">{label}</div><div className={`text-sm text-slate-700 ${mono ? "font-mono" : ""} whitespace-pre-wrap break-words`}>{value || "—"}</div></div>
);

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="w-36 h-8 text-xs bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">Alle · {placeholder}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function BulkSelect({ placeholder, onChange, options, testid }) {
  return (
    <Select onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-40 h-8 text-xs bg-white text-black"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
